import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont, pbdqPageInline } from './theme/pbdqTokens';
import { sdsColors, sdsRadius, SDS_RAILS, sdsBtnBase } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';
import PbdqGradientStripe from './components/PbdqGradientStripe.comp';
import PbdqArrowRightIcon from './components/PbdqArrowRightIcon.comp';
import PbdqOutcomeCard from './components/PbdqOutcomeCard.comp';
import PbdqStoryCarousel from './components/PbdqStoryCarousel.comp';
import PbdqHeroDashboardCard from './components/PbdqHeroDashboardCard.comp';
import HomeHeroDashboard from '../../assets/images/PoweredByDQ/HomeHeroDashboard.png';

import HomeOutcomeCosAssessments from '../../assets/images/PoweredByDQ/HomeOutcomeCosAssessments.jpg';
import HomeOutcomeSchoolCosReadiness from '../../assets/images/PoweredByDQ/HomeOutcomeSchoolCosReadiness.jpg';
import HomeOutcomeLearnCreator from '../../assets/images/PoweredByDQ/HomeOutcomeLearnCreator.jpg';
import HomeOutcomeWorkforceAiReadiness from '../../assets/images/PoweredByDQ/HomeOutcomeWorkforceAiReadiness.jpg';
import HomeOutcomeProgramValidation from '../../assets/images/PoweredByDQ/HomeOutcomeProgramValidation.jpg';
import HomeWayDqPro from '../../assets/images/PoweredByDQ/HomeWayDqPro_v2.jpg';
import HomeWayDirect from '../../assets/images/PoweredByDQ/HomeWayDirect_v2.jpg';
import HomeStoryLogo1 from '../../assets/images/PoweredByDQ/HomeStoryLogo1.png';
import HomeStoryImage1 from '../../assets/images/PoweredByDQ/HomeStoryImage1.jpg';
import HomeStoryLogo2 from '../../assets/images/PoweredByDQ/HomeStoryLogo2.png';
import HomeStoryImage2 from '../../assets/images/PoweredByDQ/HomeStoryImage2.jpg';
import HomeStoryLogo3 from '../../assets/images/PoweredByDQ/HomeStoryLogo3.png';
import HomeStoryImage3 from '../../assets/images/PoweredByDQ/HomeStoryImage3.jpg';
import HomeStoryLogo4 from '../../assets/images/PoweredByDQ/HomeStoryLogo4.png';
import HomeStoryImage4 from '../../assets/images/PoweredByDQ/HomeStoryImage4.jpg';
import HomeStoryLogo5 from '../../assets/images/PoweredByDQ/HomeStoryLogo5.png';
import HomeStoryImage5 from '../../assets/images/PoweredByDQ/HomeStoryImage5.jpg';
import HomeStoryLogo6 from '../../assets/images/PoweredByDQ/HomeStoryLogo6.png';
import HomeStoryImage6 from '../../assets/images/PoweredByDQ/HomeStoryImage6.jpg';
import HomeStoryLogo7 from '../../assets/images/PoweredByDQ/HomeStoryLogo7.png';
import HomeStoryImage7 from '../../assets/images/PoweredByDQ/HomeStoryImage7.jpg';
import HomeStoryLogo8 from '../../assets/images/PoweredByDQ/HomeStoryLogo8.png';
import HomeStoryImage8 from '../../assets/images/PoweredByDQ/HomeStoryImage8.jpg';

// This page now follows the Solutions page's design system (rust orange /
// charcoal, solutionsDesignSystem.js) per an exact reference mockup, so the
// two pages read as one consistent product. The "Built on IEEE 3527.1™"
// band is the one deliberate exception — kept on its existing navy/marquee
// treatment exactly as it was, per explicit instruction.
const useStyles = makeStyles(() => ({
    accentText: {
        color: sdsColors.accent,
    },
    accentHighlight: {
        color: sdsColors.accent,
    },

    heroSectionWrapper: {
        position: 'relative',
        overflow: 'hidden',
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
    },
    heroContainer: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(28px, 3.5vw, 40px)',
    },
    hero: {
        position: 'relative',
    },
    heroGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(32px, 4vw, 56px) clamp(24px, 3vw, 48px)',
        alignItems: 'center',
    },
    heroLeft: {
        flex: '1.15 1 480px',
        maxWidth: 720,
        minWidth: 0,
        position: 'relative',
        zIndex: 1,
    },
    heroTitle: {
        margin: 0,
        fontSize: 'clamp(32px, 3.6vw, 54px)',
        fontWeight: 800,
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        color: sdsColors.text,
    },
    heroTitleLine: {
        display: 'block',
    },
    heroTitleLineNowrap: {
        display: 'block',
        whiteSpace: 'nowrap',
        '@media (max-width: 580px)': {
            whiteSpace: 'normal',
        },
    },
    heroLead: {
        maxWidth: '52ch',
        margin: '24px 0 0',
        fontSize: 'clamp(17px, 1.4vw, 20px)',
        lineHeight: 1.5,
        color: sdsColors.textMuted,
    },
    heroCtas: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: 32,
    },
    btnHeroPrimary: {
        ...sdsBtnBase,
        height: 54,
        padding: '0 26px',
        borderRadius: sdsRadius.pill,
        background: sdsColors.accent,
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        '&:hover': {
            background: sdsColors.accentDark,
            color: '#fff',
            textDecoration: 'none',
        },
    },
    btnHeroOutline: {
        ...sdsBtnBase,
        height: 54,
        padding: '0 26px',
        borderRadius: sdsRadius.pill,
        background: '#fff',
        color: sdsColors.text,
        border: `1px solid ${sdsColors.border}`,
        fontSize: 16,
        fontWeight: 600,
        '&:hover': {
            borderColor: sdsColors.text,
            background: '#F8F9FB',
            color: sdsColors.text,
            textDecoration: 'none',
        },
    },
    heroStatsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '18px 0',
        marginTop: 'clamp(24px, 3vw, 36px)',
    },
    statCol1: {
        paddingRight: 28,
        borderRight: '1px solid #D9DEE5',
        minWidth: 0,
    },
    statCol2: {
        paddingLeft: 28,
        minWidth: 0,
    },
    statEyebrow: {
        display: 'block',
        fontSize: 13,
        fontWeight: 600,
        lineHeight: 1.2,
        color: sdsColors.textMuted,
        marginBottom: 6,
    },
    statNum: {
        display: 'block',
        fontSize: 32,
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: sdsColors.text,
        fontVariantNumeric: 'tabular-nums',
    },
    statLabel: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1.4,
        color: sdsColors.textMuted,
        marginTop: 8,
    },
    heroArt: {
        position: 'relative',
        flex: '1.2 1 520px',
        minWidth: 0,
        display: 'grid',
        margin: '-24px -4% -24px 0',
        paddingLeft: 'clamp(16px, 3vw, 48px)',
        '@media (max-width: 820px)': {
            margin: '0',
            paddingLeft: 0,
            width: '100%',
        },
    },
    heroArtImg: {
        width: '106%',
        maxWidth: 'none',
        height: 'auto',
        display: 'block',
        '@media (max-width: 820px)': {
            width: '100%',
        },
    },

    // The Define/Measure/Build/Validate/Benchmark row, styled like the
    // Solutions page's tab cards (colored rail + badge + title + desc)
    // instead of a vertical side panel.
    capabilitySection: {
        marginTop: 48,
    },
    capabilityGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 16,
    },
    capabilityCard: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: 12,
        minHeight: 188,
        padding: '28px 20px 24px',
        boxSizing: 'border-box',
        background: sdsColors.surface,
        border: `1px solid ${sdsColors.border}`,
        borderRadius: sdsRadius.md,
        overflow: 'hidden',
    },
    capabilityRail: {
        position: 'absolute',
        left: 0,
        top: 0,
        right: 0,
        height: 4,
    },
    capabilityBadge: {
        width: 56,
        height: 56,
        borderRadius: sdsRadius.pill,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 14,
        color: sdsColors.text,
    },
    capabilityTitle: {
        fontWeight: 700,
        fontSize: 16,
        color: sdsColors.text,
        margin: 0,
    },
    capabilityDesc: {
        fontSize: 14,
        lineHeight: 1.45,
        color: sdsColors.textSubtle,
        margin: 0,
    },

    // The "Built on IEEE 3527.1™" slim marquee strip per exact mockup
    standardsBand: {
        background: '#F4F6F9',
        borderTop: '1px solid #E9ECF0',
        borderBottom: '1px solid #E9ECF0',
    },
    standardsBandInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: '22px clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '16px clamp(20px, 3vw, 40px)',
    },
    standardsBuiltOnCol: {
        flex: 'none',
        paddingRight: 'clamp(16px, 3vw, 40px)',
        borderRight: '1px solid #D9DEE5',
    },
    standardsBuiltOnText: {
        display: 'block',
        fontSize: 13,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    standardsBuiltOnHeading: {
        display: 'block',
        fontSize: 32,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: sdsColors.accent,
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    standardsBuiltOnSub: {
        display: 'block',
        fontSize: 11.5,
        lineHeight: 1.35,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    standardsBandMarqueeMask: {
        flex: '1 1 300px',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
        maskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
        WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent)',
    },
    standardsBandMarqueeTrack: {
        display: 'flex',
        gap: 'clamp(32px, 4vw, 56px)',
        width: 'max-content',
        alignItems: 'center',
        animation: '$dqStandardsSlide 240s linear infinite',
        '&:hover': {
            animationPlayState: 'paused',
        },
    },
    '@keyframes dqStandardsSlide': {
        from: { transform: 'translateX(0)' },
        to: { transform: 'translateX(-50%)' },
    },
    standardsItem: {
        fontSize: 15,
        fontWeight: 700,
        letterSpacing: '-0.01em',
        whiteSpace: 'nowrap',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },

    section: {
        padding: 'clamp(36px, 4.5vw, 56px) 0',
    },
    sectionHeader: {
        textAlign: 'left',
        margin: 0,
        display: 'grid',
    },
    sectionChip: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 16,
        fontFamily: pbdqFont,
        background: 'none',
        padding: 0,
    },
    sectionChipSurface: {
        background: 'none',
        border: 'none',
    },
    h2: {
        fontSize: 'clamp(28px, 3.4vw, 46px)',
        lineHeight: 1.06,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    lead: {
        fontSize: 16,
        lineHeight: 1.55,
        margin: '16px 0 0',
        maxWidth: 'none',
        color: '#5B6470',
        fontFamily: pbdqFont,
        whiteSpace: 'nowrap',
        '@media (max-width: 1024px)': {
            whiteSpace: 'normal',
        },
    },

    outcomesGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 44,
    },
    agentCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 14,
        padding: '32px 28px',
        borderRadius: 20,
        background: '#FBEDE4',
        border: '1px solid #F5D9C8',
    },
    agentIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: '#FFFFFF',
        color: '#A63C0C',
    },
    agentTitle: {
        fontSize: 22,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    agentDesc: {
        fontSize: 15,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        maxWidth: '30ch',
        fontFamily: pbdqFont,
    },
    agentBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        border: '1.5px solid #ED6628',
        background: '#FFFFFF',
        color: '#A63C0C',
        textDecoration: 'none',
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        marginTop: 6,
        cursor: 'pointer',
        fontFamily: pbdqFont,
        transition: 'background 160ms ease',
        '&:hover': {
            background: '#FDECE3',
            color: '#A63C0C',
        },
    },

    // A full-bleed tinted band, breaking out of the page container.
    waysSection: {
        background: '#F4F6F9',
    },
    waysSectionInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
    },
    waysGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'clamp(20px, 2.5vw, 32px)',
        marginTop: 44,
    },
    wayCard: {
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        background: '#FFFFFF',
    },
    wayImageWrap: {
        padding: '12px 12px 0',
    },
    wayImage: {
        display: 'block',
        width: '100%',
        height: 'clamp(200px, 18vw, 260px)',
        objectFit: 'cover',
        borderRadius: 12,
    },
    wayBody: {
        padding: '28px clamp(20px, 2.4vw, 32px) clamp(24px, 2.4vw, 32px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        flex: 1,
    },
    wayTag1: {
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        background: '#FDECE3',
        color: '#A63C0C',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    wayTag2: {
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 12px',
        borderRadius: 999,
        background: '#EEF3F8',
        color: '#2A4057',
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
    },
    wayTitle: {
        fontSize: 24,
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: 700,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    wayDesc: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
        flex: 1,
        fontFamily: pbdqFont,
    },
    wayCtaPrimary: {
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 700,
        fontFamily: pbdqFont,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    wayCtaOutline: {
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        height: 48,
        padding: '0 22px',
        borderRadius: 999,
        background: '#FFFFFF',
        color: '#1B2430',
        border: '1px solid #D5D9E0',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: pbdqFont,
        cursor: 'pointer',
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1), background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
            background: '#F8F9FB',
            color: '#1B2430',
            textDecoration: 'none',
        },
    },

    seeAllLink: {
        display: 'inline-block',
        fontSize: 15,
        fontWeight: 600,
        textDecoration: 'none',
        color: sdsColors.accentDark,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    seeAllWrap: {
        textAlign: 'center',
        marginTop: 28,
    },
    statsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
        gap: '20px 0',
        marginTop: 44,
        borderTop: '2px solid #1B2430',
    },
    statCol: {
        padding: '24px 28px 0',
        borderLeft: '1px solid #E6E9EE',
        '&:first-child': {
            padding: '24px 28px 0 0',
            borderLeft: 'none',
        },
        '&:last-child': {
            padding: '24px 0 0 28px',
        },
        '@media (max-width: 800px)': {
            padding: '16px 0',
            borderLeft: 'none',
            borderBottom: '1px solid #E6E9EE',
        },
    },
    statValue: {
        fontSize: 'clamp(30px, 3vw, 42px)',
        lineHeight: 1.05,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        color: sdsColors.accent,
        margin: 0,
        fontVariantNumeric: 'tabular-nums',
        fontFamily: pbdqFont,
    },
    statLabel: {
        fontSize: 12.5,
        lineHeight: 1.45,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#5B6470',
        margin: '12px 0 0',
        maxWidth: '24ch',
        fontFamily: pbdqFont,
        fontWeight: 600,
    },

    ctaSection: {
        background: '#172939',
        color: '#FFFFFF',
    },
    ctaInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '32px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    ctaContent: {
        flex: '1 1 440px',
        minWidth: 0,
    },
    ctaTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    ctaTitleMuted: {
        display: 'block',
        color: '#8AA3BC',
    },
    ctaLead: {
        fontSize: 16.5,
        lineHeight: 1.6,
        maxWidth: '58ch',
        margin: '24px 0 0',
        color: '#D9E3EE',
        fontFamily: pbdqFont,
    },
    ctaActions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
    },
    btnCtaPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        border: 0,
        cursor: 'pointer',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 700,
        fontFamily: pbdqFont,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
    btnCtaOutline: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 52,
        padding: '0 24px',
        borderRadius: 999,
        background: 'transparent',
        color: '#FFFFFF',
        border: '1px solid #5D7A96',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 600,
        fontFamily: pbdqFont,
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1), background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#FFFFFF',
            background: 'rgba(255, 255, 255, 0.06)',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },

    btnOutline: {
        ...sdsBtnBase,
        background: sdsColors.surface,
        color: sdsColors.accentDark,
        borderColor: sdsColors.accent,
        '&:hover': {
            background: sdsColors.imagePlaceholder,
            color: sdsColors.accentDark,
            textDecoration: 'none',
        },
    },
    btnText: {
        ...sdsBtnBase,
        background: 'transparent',
        color: sdsColors.textMuted,
        '&:hover': {
            color: sdsColors.text,
            textDecoration: 'none',
        },
    },
    btnPrimary: {
        ...sdsBtnBase,
        background: sdsColors.accent,
        color: '#fff',
        '&:hover': {
            background: sdsColors.accentDark,
            color: '#fff',
            textDecoration: 'none',
        },
    },
    btnTextOnDark: {
        ...sdsBtnBase,
        background: 'transparent',
        color: '#fff',
        '&:hover': {
            color: '#fff',
            textDecoration: 'underline',
        },
    },
    btnOnDark: {
        ...sdsBtnBase,
        background: '#fff',
        color: sdsColors.accentDark,
        '&:hover': {
            background: sdsColors.bg,
            color: sdsColors.accentDark,
            textDecoration: 'none',
        },
    },
}));

const OUTCOME_CARDS = [
    {
        index: '01',
        title: 'Measure your country\'s child online safety',
        description: 'Understand children’s readiness, risk exposure, and government readiness—then identify where coordinated action should focus.',
        linkLabel: 'Child Online Safety (COS) Assessments',
        to: pbdqRoutes.cosAssessments,
        image: HomeOutcomeCosAssessments,
        imageAlt: 'Children using devices in a classroom',
    },
    {
        index: '02',
        title: 'Grow student online safety readiness',
        description: 'Connect student assessment, targeted learning, delivery, and progress tracking in one readiness system.',
        linkLabel: 'School Child Online Safety (COS) Readiness',
        to: pbdqRoutes.schoolCosReadiness,
        image: HomeOutcomeSchoolCosReadiness,
        imageAlt: 'Students learning online safety at school',
    },
    {
        index: '03',
        title: 'Create AI-digital literacy learning',
        description: 'Create standards-aligned learning programs with embedded assessment—tailored to your audience, curriculum, language and context.',
        linkLabel: 'LearnCreator',
        to: pbdqRoutes.learnCreator,
        image: HomeOutcomeLearnCreator,
        imageAlt: 'Teacher creating a program',
    },
    {
        index: '04',
        title: 'Build workforce AI readiness',
        description: 'Set role-based expectations, measure capability, build targeted upskilling, and track progress.',
        linkLabel: 'Workforce AI Readiness',
        to: pbdqRoutes.workforceAiReadiness,
        image: HomeOutcomeWorkforceAiReadiness,
        imageAlt: 'Employees in an AI workshop',
    },
    {
        index: '05',
        title: 'Validate program quality and impact',
        description: 'Validate your program against global standards, earn recognition where qualified, and measure its impact before and after delivery.',
        linkLabel: 'Program Validation',
        to: pbdqRoutes.programValidation,
        image: HomeOutcomeProgramValidation,
        imageAlt: 'Program review meeting',
    },
];

const STANDARDS_MARQUEE = [
    'UNESCO - AI competency framework for students',
    'European Commission - DigComp 3.0',
    'OECD - AI Literacy Framework for Primary and Secondary Education',
    'U.S. DoL - Artificial Intelligence Literacy Framework',
    'China MoE - Guide to Using Generative Artificial Intelligence',
    'OECD - PISA 2029',
    'The Alan Turing Institute - AI Skills for Business Competency Framework',
    'Digital Education Council (DEC) - AI Literacy Framework',
    'NIST - NICE Framework',
    'Educational Testing Service (ETS) - Preparing K–12 Students With AI Literacy',
    'IEEE - Standard for Data and AI Literacy, Skills, and Readiness',
    'ITU - Digital Skills Toolkit',
    'ISTE - Standards for Educators: Computational Thinking Competencies',
    'AI4K12 - Five Big Ideas in AI',
    'Singapore MoE - Cyber Wellness 101',
    'Korea Education and Research Information Service (KERIS) - Framework for Assessing AI and Digital Literacy',
    'Australia DoE - Framework for Generative Artificial Intelligence in Schools',
    'UAE MoE - National AI Literacy Curriculum Framework',
    'GSMA - Agentic AI for Telecom',
    'AACSB - A Framework for Human-Centric AI',
    'IEA - International Computer and Information Literacy Study Framework',
];

const HERO_PANEL_ITEMS = [
    { title: 'Define', desc: 'See what AI-digital capacity means' },
    { title: 'Measure', desc: 'Assess current capabilities and establish a baseline' },
    { title: 'Build', desc: 'Create targeted programs based on assessment insights' },
    { title: 'Validate', desc: 'Validate your programs against global standards' },
    { title: 'Benchmark', desc: 'Compare your results with data from 100+ countries' },
];

const STORIES = [
    {
        orgName: 'Global Cybersecurity Forum',
        logo: HomeStoryLogo1,
        logoScale: 1.4,
        image: HomeStoryImage1,
        tag: 'COS Assessments',
        title: 'A global mission to cut child cyber-risk by 15% in five years',
        body: 'Global Cybersecurity Forum (GCF) Foundation, supported by the Saudi National Cybersecurity Authority (NCA), and with the ITU, UNICEF, and WeProtect, built a unified global standard and action framework measuring how resilient a nation’s child online safety system is across its whole ecosystem, with plans to distribute Child Online Safety (COS) Assessments to 100+ countries.',
        link: 'https://www.spa.gov.sa/ru/N2412817',
    },
    {
        orgName: 'Gyeongsangbuk-do',
        logo: HomeStoryLogo2,
        image: HomeStoryImage2,
        tag: 'Workforce AI Readiness · LearnCreator',
        title: 'In preparation for APEC 2025, Gyeongsangbuk-do developed globally interoperable AI-literacy standards.',
        body: 'South Korea’s largest province, Gyeongsangbuk-do, deployed all five capabilities — Define, Measure, Build, Validate, Benchmark — as a globally interoperable ecosystem, powered by DQ, owned end-to-end by the government and positioned as a model for other APEC economies.',
        link: 'https://www.localnewsroom.co.kr/news/articleView.html?idxno=7363',
    },
    {
        orgName: 'The LEGO Group',
        logo: HomeStoryLogo3,
        image: HomeStoryImage3,
        tag: 'DQ Stars · LearnCreator',
        title: 'LEGO taught millions of children digital empathy, on the global standard',
        body: 'The LEGO group presents children with real digital dilemmas — cyberbullying, online mobs, misinformation — in an interactive experience built on the DQ framework, awarding an ‘online empathy hero’ status.',
        link: 'https://www.lego.com/en-us/aboutus/news/2021/october/digital-citizenship',
    },
    {
        orgName: 'NUS',
        logo: HomeStoryLogo4,
        image: HomeStoryImage4,
        tag: 'Frameworks and indices',
        title: 'NUS developed digital-wellbeing research to inform national measurement and policy across Singapore society.',
        body: 'The National University of Singapore developed the Digital Wellbeing Indicator Framework, powered by DQ, giving academics and educators a common, globally aligned language for measuring and building digital wellbeing competencies.',
        link: 'https://ctic.nus.edu.sg/livingwell.html',
    },
    {
        orgName: 'TikTok',
        logo: HomeStoryLogo5,
        logoScale: 0.78,
        image: HomeStoryImage5,
        tag: 'Family Safety Toolkit · Powered by DQ',
        title: 'TikTok turned digital-safety standards into practical tools for families across Asia-Pacific',
        body: 'TikTok developed the Family Safety Toolkit, Powered by DQ, translating digital citizenship principles into ten practical actions parents and teenagers complete together. Launched for Safer Internet Day 2021 through TikTok’s in-app Safety Center, with documented launches in Indonesia, Malaysia and the Philippines.',
        link: 'https://www.tiktok.com/safety',
    },
    {
        orgName: 'Alannah & Madeline Foundation',
        logo: HomeStoryLogo6,
        image: HomeStoryImage6,
        tag: 'LearnCreator, DQ Stars',
        title: 'eSmart brought standards-aligned digital citizenship to schools across Australia',
        body: 'The Alannah & Madeline Foundation, supported by Accenture, upgraded its eSmart Digital License+ using powered by DQ, giving Australian schools an evidence-based way to build and measure students’ digital-citizenship capabilities at national scale.',
        link: 'https://www.amf.org.au/news/6-million-in-funding-to-roll-out-new-esmart-lessons-protecting-children-online',
    },
    {
        orgName: 'World Bank',
        logo: HomeStoryLogo7,
        image: HomeStoryImage7,
        tag: 'Workforce assessment · Frameworks',
        title: 'From teacher training to system transformation in the Kyrgyz Republic',
        body: 'Working with the World Bank, PbDQ developed a five-step methodology for a national digital-skills system in the Kyrgyz Republic. A pilot combining 80 hours of teacher training with 32 weeks of mentoring moved teachers from B1 Integrator to B2 Expert on DigCompEdu — 36,000 teachers trained, 80% women.',
        link: 'https://www.worldbank.org/en/results/2026/04/15/building-digital-skills-to-jobs-pathways-for-citizens-teachers-and-students-in-europe-and-central-asia',
    },
    {
        orgName: 'UAE Ministry of Education',
        logo: HomeStoryLogo8,
        image: HomeStoryImage8,
        tag: 'FrameworkCreator · DQ framework',
        title: 'A K–12 digital-literacy framework connecting AI and sustainability',
        body: 'The UAE Ministry of Education, with the DQ Institute, developed the Digital Literacy Cross-Curricular Framework: four connected domains translating the DQ framework underlying IEEE 3527.1™ into age-appropriate learning outcomes across subjects from kindergarten to Grade 12.',
        link: 'https://moe.gov.ae/en/pages/ai.aspx',
    },
];

const STATS = [
    { value: '15+ yrs', label: 'of dedicated work since 2009' },
    { value: 'IEEE 3527.1', label: 'the global standard, used in 100+ countries' },
    { value: '1,000+', label: 'frameworks and programs mapped to DQ' },
    { value: '2.3B+', label: 'estimated media reach' },
];

// A "certified standard" glyph for the standards band's icon badge.
const ShieldCheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l7 3v6c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
    </svg>
);
// A small checkmark for each "aligned with" pill in the standards marquee.
const CheckIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 6L9 17l-5-5" />
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
);
const ChatIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-4-.9L3 21l1.9-5.5A8.5 8.5 0 1 1 21 11.5z" />
    </svg>
);

const PbdqHomePageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();
    const openAgentChat = useAgentChat();

    return (
        <div className={shared.page} style={{ background: sdsColors.bg }}>
            <PbdqNav />
            {/* <PbdqGradientStripe sticky /> */}
            <div className={classes.heroSectionWrapper}>
                <div className={classes.heroContainer}>
                    <section className={classes.hero}>
                        <div className={classes.heroGrid}>
                            <div className={classes.heroLeft}>
                                <h1 className={classes.heroTitle}>
                                    <span className={classes.heroTitleLineNowrap}>
                                        Build <span className={classes.accentHighlight}>AI-digital capacity</span>
                                    </span>
                                    <span className={classes.heroTitleLine}>at the scale you need</span>
                                </h1>
                                <p className={classes.heroLead}>
                                    Define, measure, build, validate and benchmark AI-digital capability — for a country, a school network or a workforce.
                                </p>
                                <div className={classes.heroCtas}>
                                    <Link to={pbdqRoutes.getStarted} className={classes.btnHeroPrimary}>
                                        Get started with PbDQ <PbdqArrowRightIcon />
                                    </Link>
                                    <Link to={pbdqRoutes.solutions} className={classes.btnHeroOutline}>
                                        Explore solutions
                                    </Link>
                                </div>
                                <div className={classes.heroStatsRow}>
                                    <div className={classes.statCol1}>
                                        <span className={classes.statEyebrow}>Aligned with</span>
                                        <span className={classes.statNum}>1000+</span>
                                        <span className={classes.statLabel}>global and national<br />frameworks and programs</span>
                                    </div>
                                    <div className={classes.statCol2}>
                                        <span className={classes.statEyebrow}>Used in</span>
                                        <span className={classes.statNum}>100+</span>
                                        <span className={classes.statLabel}>Countries</span>
                                    </div>
                                </div>
                            </div>

                            <div className={classes.heroArt}>
                                <img
                                    src={HomeHeroDashboard}
                                    alt="The Powered by DQ capability dashboard on a laptop, in front of a glowing map of the world"
                                    className={classes.heroArtImg}
                                />
                            </div>

                            {/* Previous interactive hero dashboard card kept for reference:
                            <PbdqHeroDashboardCard />
                            */}
                        </div>
                    </section>
                </div>
            </div>

            <section className={classes.standardsBand} aria-label="Standards">
                <div className={classes.standardsBandInner}>
                    <div className={classes.standardsBuiltOnCol}>
                        <span className={classes.standardsBuiltOnText}>Built on</span>
                        <span className={classes.standardsBuiltOnHeading}>IEEE 3527.1™</span>
                        <span className={classes.standardsBuiltOnSub}>AI-digital competency<br />standard</span>
                    </div>
                    <div className={classes.standardsBandMarqueeMask}>
                        <div className={classes.standardsBandMarqueeTrack}>
                            {[...STANDARDS_MARQUEE, ...STANDARDS_MARQUEE, ...STANDARDS_MARQUEE, ...STANDARDS_MARQUEE].map((label, i) => (
                                <span
                                    key={`${label}-${i}`}
                                    className={classes.standardsItem}
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <div className={classes.heroContainer}>
                <section id="outcomes" aria-label="Choose an outcome" className={classes.section}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionChip}>Start with what you need to achieve</span>
                        <h2 className={classes.h2}>Choose an <span className={classes.accentText}>outcome</span> first.</h2>
                        <p className={classes.lead}>
                            We’ll show you the solution, the result it produces, and the right commercial route for your scale and intended use.
                        </p>
                    </div>
                    <div className={classes.outcomesGrid}>
                        {OUTCOME_CARDS.map((card, i) => (
                            <PbdqOutcomeCard key={card.index} {...card} railColor={SDS_RAILS[i % SDS_RAILS.length].rail} />
                        ))}
                        <div className={classes.agentCard}>
                            <span className={classes.agentIcon}><ChatIcon /></span>
                            <p className={classes.agentTitle}>Not sure where to start?</p>
                            <p className={classes.agentDesc}>Tell the DQ Agent what you want to achieve and it will point you to the right solution.</p>
                            <button type="button" onClick={openAgentChat} className={classes.agentBtn}>Talk to the DQ Agent</button>
                        </div>
                    </div>
                </section>
            </div>

            <section id="ways" aria-label="Two ways to work with us" className={classes.waysSection}>
                <div className={classes.waysSectionInner}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionChip}>Two ways to work with us</span>
                        <h2 className={classes.h2}>
                            <span style={{ display: 'block', color: sdsColors.accent }}>Take Powered by DQ to market—</span>
                            <span style={{ display: 'block' }}>or deploy it for your own people.</span>
                        </h2>
                    </div>
                    <div className={classes.waysGrid}>
                        <div className={classes.wayCard}>
                            <div className={classes.wayImageWrap}>
                                <img src={HomeWayDqPro} alt="DQ Pro partner presenting to a client" className={classes.wayImage} />
                            </div>
                            <div className={classes.wayBody}>
                                <span className={classes.wayTag1}>1 · Become a DQ Pro</span>
                                <h3 className={classes.wayTitle}>Take PbDQ to your market</h3>
                                <p className={classes.wayDesc}>
                                    If you serve governments, schools, companies or other clients, become a DQ Pro to access wholesale pricing, create your own service packages and grow a business around Powered by DQ.
                                </p>
                                <Link to={`${pbdqRoutes.pricing}#dq-pro`} className={classes.wayCtaPrimary}>
                                    Grow your business with PbDQ
                                </Link>
                            </div>
                        </div>
                        <div className={classes.wayCard}>
                            <div className={classes.wayImageWrap}>
                                <img src={HomeWayDirect} alt="Team deploying in their organization" className={classes.wayImage} />
                            </div>
                            <div className={classes.wayBody}>
                                <span className={classes.wayTag2}>2 · Purchase directly</span>
                                <h3 className={classes.wayTitle}>Deploy for your own people</h3>
                                <p className={classes.wayDesc}>
                                    If you are purchasing for your organization’s own employees, students, members or communities, work with us directly to configure the right solution and deployment.
                                </p>
                                <button type="button" onClick={openAgentChat} className={classes.wayCtaOutline}>
                                    Deploy in your organization
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className={classes.heroContainer}>
                <section id="impact" aria-label="Impact" className={classes.section}>
                    <div className={classes.sectionHeader}>
                        <span className={classes.sectionChip}>Impact</span>
                        <h2 className={classes.h2}>See what others have built with <span className={classes.accentText}>Powered by DQ</span></h2>
                    </div>
                    <div className={classes.statsGrid}>
                        {STATS.map((stat) => (
                            <div key={stat.value} className={classes.statCol}>
                                <p className={classes.statValue}>{stat.value}</p>
                                <p className={classes.statLabel}>{stat.label}</p>
                            </div>
                        ))}
                    </div>
                    <PbdqStoryCarousel stories={STORIES} />
                </section>
            </div>

            <section id="cta" aria-label="Next step" className={classes.ctaSection}>
                <div className={classes.ctaInner}>
                    <div className={classes.ctaContent}>
                        <h2 className={classes.ctaTitle}>
                            <span style={{ display: 'block' }}>Tell us the outcome, scale, and route.</span>
                            <span className={classes.ctaTitleMuted}>We’ll show you the next step.</span>
                        </h2>
                        {/* <p className={classes.ctaLead}>
                            Configure your planned deployment to receive a DQ Pro wholesale estimate or, for qualified large direct buyers, an enterprise pricing range. Standard information appears immediately; custom services are clearly identified for confirmation.
                        </p> */}
                    </div>
                    <div className={classes.ctaActions}>
                        <button type="button" onClick={openAgentChat} className={classes.btnCtaPrimary}>
                            Talk to the DQ Agent
                        </button>
                        <Link to={pbdqRoutes.pricing} className={classes.btnCtaOutline}>
                            Become a DQ Pro <ArrowRightIcon />
                        </Link>
                    </div>
                </div>
            </section>

            {/* <PbdqGradientStripe /> */}
            <PbdqFooter />
        </div>
    );
};

export default PbdqHomePageContent;
